import { z } from "zod";

const identifierSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/);

const valueVSchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,21})(\.\d{1,2})?$/, "V must be a plain decimal string with at most 2 decimal places")
  .refine((value) => !/^0(?:\.0+)?$/.test(value), "V must be greater than zero");

const coefficientSchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,7})(\.\d{1,12})?$/, "Coefficient must be a plain decimal string with at most 12 decimal places")
  .refine((value) => !/^0(?:\.0+)?$/.test(value), "Coefficient must be greater than zero");

export const resolvedQItemInputSchema = z
  .object({
    code: identifierSchema,
    value: coefficientSchema,
  })
  .strict();

export const resolvedComponentInputSchema = z
  .object({
    componentId: identifierSchema,
    label: z.string().trim().min(1).max(200),
    valueV: valueVSchema,
    complexityG: coefficientSchema,
    qItems: z.array(resolvedQItemInputSchema).min(1).max(200),
  })
  .strict()
  .superRefine((component, context) => {
    const codes = new Set<string>();
    component.qItems.forEach((item, index) => {
      if (codes.has(item.code)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate Q code: ${item.code}`,
          path: ["qItems", index, "code"],
        });
      }
      codes.add(item.code);
    });
  });

export const resolvedCalculationInputSchema = z
  .object({
    rulesetVersion: identifierSchema,
    components: z.array(resolvedComponentInputSchema).min(1).max(100),
  })
  .strict()
  .superRefine((input, context) => {
    const ids = new Set<string>();
    input.components.forEach((component, index) => {
      if (ids.has(component.componentId)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate component ID: ${component.componentId}`,
          path: ["components", index, "componentId"],
        });
      }
      ids.add(component.componentId);
    });
  });

export type ResolvedQItemInput = z.infer<typeof resolvedQItemInputSchema>;
export type ResolvedComponentInput = z.infer<typeof resolvedComponentInputSchema>;
export type ResolvedCalculationInput = z.infer<typeof resolvedCalculationInputSchema>;
